import type { UnknownAction } from '@reduxjs/toolkit'
import type { BaseQueryFn } from '@reduxjs/toolkit/query'

import { resourceScopeCleared } from '../session/scope'
import { ResourceScopeTracker } from '../session/resourceScopeSignals'
import type { HttpClient } from './httpClient'
import type { RequestObserver, ResponseHeaders, TransportError, TransportRequest, TransportResult } from './transport'

/**
 * Что runtime получает от composition через `extraArgument` стора. Transport —
 * не глобальная переменная модуля: тест собирает стор со своим fetch, а
 * приложение — с настоящим, и ни одна из сборок не видит чужой.
 */
export interface ClientRuntimeExtra {
  http: HttpClient
}

/** Мета результата: корреляция успешного ответа для журнала/отчёта. */
export interface TransportMeta {
  correlationId: string
  status: number
  /**
   * Заголовки ответа из закрытого списка (`EXPOSED_RESPONSE_HEADERS`), имена в
   * нижнем регистре: свежесть кэша службы (`x-resource-freshness`,
   * `x-resource-observed-at`, `x-resource-stale-reason`) и т. п.
   */
  headers?: ResponseHeaders
}

/** Transport стора — для baseQuery и для команд, которые не кладут ответ в кэш (`requestFile`). */
export function httpOf(extra: unknown): HttpClient {
  const http = (extra as Partial<ClientRuntimeExtra> | undefined)?.http
  if (http === undefined) {
    throw new Error('Стор собран без transport: передайте { http } в thunk.extraArgument (createClientStore)')
  }
  return http
}

/** Наблюдатель области — на transport стора: два стора (тест и приложение) не делят память о rid. */
const scopeTrackers = new WeakMap<HttpClient, ResourceScopeTracker>()

function scopeTrackerOf(http: HttpClient): ResourceScopeTracker {
  let tracker = scopeTrackers.get(http)
  if (tracker === undefined) {
    tracker = new ResourceScopeTracker()
    scopeTrackers.set(http, tracker)
  }
  return tracker
}

/**
 * Запрос transport стора с наблюдением области. Ресурс вышел из работы
 * (выключен, удалён, допуск роли снят) — граница области (D-02 аудита AUD-P):
 * слайсы пакетов стирают черновики rid, оболочка — вкладки и кэш. Сигнал —
 * сам ответ сервера; через эту функцию идут и кэш RTK, и команды мимо него.
 */
export async function observedRequest<T>(
  http: HttpClient,
  request: TransportRequest,
  signal: AbortSignal | undefined,
  dispatch: (action: UnknownAction) => unknown,
  observer?: RequestObserver,
): Promise<TransportResult<T>> {
  const tracker = scopeTrackerOf(http)
  const started = tracker.begin()
  const result = await http.request<T>(request, signal, observer)
  for (const resourceId of tracker.observe(request, result.ok ? { ok: true, data: result.data } : result, started)) {
    dispatch(resourceScopeCleared({ resourceId }))
  }
  return result
}

/**
 * baseQuery единственного `createApi`. Ошибка — `TransportError`, одна на весь
 * клиент; экран различает её по `kind`, а не по числу статуса. Ответы реестра
 * и отказы уровня ресурса превращаются здесь в `resourceScopeCleared`
 * (`session/resourceScopeSignals.ts`).
 */
export const transportBaseQuery: BaseQueryFn<TransportRequest, unknown, TransportError, object, TransportMeta> = async (
  request,
  api,
) => {
  if (request.responseType === 'blob') {
    // Blob в кэше RTK — несериализуемое состояние Redux и файл, живущий до выхода.
    throw new Error('Ответ blob не кладётся в кэш RTK: файл берут командой requestFile и отдают на скачивание')
  }
  const result = await observedRequest(httpOf(api.extra), request, api.signal, api.dispatch)
  return result.ok
    ? {
        data: result.data,
        meta: {
          correlationId: result.correlationId,
          status: result.status,
          ...(result.headers !== undefined ? { headers: result.headers } : {}),
        },
      }
    : { error: result.error }
}
